import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { MagnifyingGlass, BookOpen, CaretRight, Bookmark, CheckCircle, Clock, FileText } from "@phosphor-icons/react";

const KIND_LABEL = { rams: "RAMS", policy: "Policy", procedure: "Procedure", statement: "Statement", guidance: "Guidance", privacy: "Privacy" };

function StatusChip({ personal }) {
  if (personal.acknowledged) return <span data-testid="chip-acknowledged" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"><CheckCircle size={13} weight="fill" /> Acknowledged</span>;
  if (personal.action_required) return <span data-testid="chip-outstanding" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"><Clock size={13} weight="bold" /> To acknowledge</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">Reference</span>;
}

function PolicyCard({ d }) {
  return (
    <Link to={`/policies/${d.id}`} data-testid={`policy-card-${d.id}`}
      className="group block bg-white border border-slate-200 rounded-xl p-4 hover:border-emerald-300 hover:shadow-sm transition-all duration-150">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-50 rounded px-1.5 py-0.5">{KIND_LABEL[d.kind] || "Doc"}</span>
          </div>
          <h3 className="font-display text-base font-bold text-slate-900 leading-snug truncate group-hover:text-emerald-900">{d.title}</h3>
          {d.summary && <p className="font-body text-sm text-slate-500 mt-1 line-clamp-2">{d.summary}</p>}
        </div>
        <CaretRight size={18} className="text-slate-300 shrink-0 group-hover:text-emerald-500 transition-colors" />
      </div>
      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-100">
        <StatusChip personal={d.personal} />
        <span className="font-mono text-[11px] text-slate-400">{d.ref}{d.publication.revision ? ` · Rev ${d.publication.revision}` : ""}</span>
      </div>
    </Link>
  );
}

export default function PolicyLibraryPage() {
  const { hasPerm } = useAuth();
  const [sections, setSections] = useState([]);
  const [docs, setDocs] = useState([]);
  const [collections, setCollections] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [docClass, setDocClass] = useState(new URLSearchParams(window.location.search).get("class") === "rams" ? "rams" : "policy");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);

  useEffect(() => {
    api.get("/policies/sections").then((r) => setSections(r.data)).catch(() => {});
    api.get("/policies/collections").then((r) => setCollections(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = {};
      if (q) params.q = q;
      if (kind) params.kind = kind;
      if (docClass) params.doc_class = docClass;
      if (status) params.status = status;
      api.get("/policies", { params })
        .then((r) => setDocs(r.data))
        .catch((e) => console.error("policy library load failed", e))
        .finally(() => { setLoading(false); setFirstLoad(false); });
    }, 300);
    return () => clearTimeout(t);
  }, [q, kind, docClass, status]);

  const bySection = useMemo(() => {
    const map = {}; const none = [];
    docs.forEach((d) => {
      if (!d.section_ids?.length) { none.push(d); return; }
      d.section_ids.forEach((sid) => { (map[sid] = map[sid] || []).push(d); });
    });
    return { map, none };
  }, [docs]);

  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Document Library</h1>
        <p className="font-body text-slate-600 mt-1 text-sm">{docClass === "rams" ? "Risk assessments and method statements, with client/job/site targeting." : "Company policies, procedures, statements, guidance and H&S manuals."}</p>
      </div>

      <div className="flex gap-2 mb-5" data-testid="doc-class-tabs">
        <button data-testid="tab-policies" onClick={() => { setDocClass("policy"); setKind(""); }}
          className={`h-10 px-4 rounded-lg text-sm font-bold border transition-colors ${docClass === "policy" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>Policies &amp; Procedures</button>
        <button data-testid="tab-rams" onClick={() => { setDocClass("rams"); setKind(""); }}
          className={`h-10 px-4 rounded-lg text-sm font-bold border transition-colors ${docClass === "rams" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>RAMS</button>
      </div>

      {collections.length > 0 && docClass === "policy" && (
        <div className="mb-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {collections.map((c) => (
            <Link key={c.id} to={`/policies/book/${c.id}`} data-testid={`collection-${c.id}`}
              className="flex items-center gap-3 bg-emerald-900 text-white rounded-xl p-4 hover:bg-emerald-800 transition-colors">
              <BookOpen size={24} weight="bold" />
              <div className="min-w-0">
                <div className="font-display font-bold truncate">{c.name}</div>
                <div className="text-xs text-emerald-100/80">{c.item_count} documents · Book reader</div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <MagnifyingGlass size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="policy-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles, references and content…" className="pl-9 h-11 bg-white border-slate-200" />
        </div>
        <select data-testid="filter-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="h-11 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="">All types</option>
          {Object.entries(KIND_LABEL).filter(([k]) => (docClass === "rams" ? ["rams", "risk_assessment", "method_statement"].includes(k) : !["rams", "risk_assessment", "method_statement"].includes(k))).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="">All statuses</option>
          <option value="outstanding">To acknowledge</option>
          <option value="acknowledged">Acknowledged</option>
        </select>
      </div>

      {firstLoad ? <p className="text-slate-400 text-sm">Loading policies…</p> : (
        <div className="flex flex-col gap-7" data-testid="policy-library">
          {loading && <p data-testid="library-updating" className="text-xs text-slate-400">Updating…</p>}
          {sections.map((s) => {
            const list = bySection.map[s.id] || [];
            if (!list.length) return null;
            return (
              <section key={s.id} data-testid={`section-${s.id}`}>
                <h2 className="font-display text-lg font-black text-slate-800 mb-3 flex items-center gap-2">
                  <span className="h-4 w-1 rounded bg-emerald-700" />{s.name}
                  <span className="font-mono text-xs font-bold text-slate-400">{list.length}</span>
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{list.map((d) => <PolicyCard key={d.id} d={d} />)}</div>
              </section>
            );
          })}
          {bySection.none.length > 0 && (
            <section data-testid="section-unfiled">
              <h2 className="font-display text-lg font-black text-slate-800 mb-3 flex items-center gap-2">
                <span className="h-4 w-1 rounded bg-slate-400" />Other documents
                <span className="font-mono text-xs font-bold text-slate-400">{bySection.none.length}</span>
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{bySection.none.map((d) => <PolicyCard key={d.id} d={d} />)}</div>
            </section>
          )}
          {docs.length === 0 && <div className="text-center py-16 text-slate-400" data-testid="library-empty"><FileText size={40} className="mx-auto mb-3 opacity-40" />{docClass === "rams" ? "No RAMS documents yet. Risk assessments and method statements will appear here." : "No documents match your search."}</div>}
        </div>
      )}
    </div>
  );
}
